import * as Sentry from '@sentry/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import apiClient from '../axios';
import type { DetectionGroup } from '../types/detection-group.js';

export interface MultiDeviceSunburstParams {
  deviceIds: string[];
  from?: string;
  to?: string;
  status?: string[];
}

export interface UseMultiDeviceSunburstOptions {
  staleTime?: number;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  enabled?: boolean;
  keepPreviousData?: boolean;
}

export interface MultiDeviceSunburstResponse {
  groups: DetectionGroup[];
  total: number;
  statusCounts: Record<string, number>;
}

const fetchMultiDeviceSunburst = async (
  params: MultiDeviceSunburstParams,
): Promise<MultiDeviceSunburstResponse> => {
  const { deviceIds, from, to, status } = params;

  try {
    const { data } = await apiClient.get<MultiDeviceSunburstResponse>(
      '/analytics/detections/sunburst',
      {
        params: {
          deviceIds: deviceIds.join(','),
          ...(from ? { from: new Date(from).toISOString() } : {}),
          ...(to ? { to: new Date(to).toISOString() } : {}),
          ...(status ? { status: status } : {}),
        },
      },
    );

    return {
      groups: data?.groups ?? [],
      total: data?.total ?? 0,
      statusCounts: data?.statusCounts ?? {},
    };
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch analytics sunburst data');
  }
};

export const useMultiDeviceSunburst = (
  params: MultiDeviceSunburstParams,
  options: UseMultiDeviceSunburstOptions = {},
) => {
  const {
    staleTime = 0,
    refetchInterval = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    enabled = true,
    keepPreviousData: shouldKeepPreviousData = false,
  } = options;

  return useQuery({
    queryKey: ['analytics-sunburst-multi', params],
    queryFn: () => fetchMultiDeviceSunburst(params),
    staleTime,
    refetchInterval,
    refetchOnWindowFocus,
    refetchOnReconnect,
    enabled: enabled && params.deviceIds.length > 0,
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
  });
};
